import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, withRouter } from 'react-router-dom'
import { Alert, Button, Card, Modal, Result, Space, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import PageLoader from '../../components/Layouts/PageLoader'
import PageTitle from '../../components/PageTitle'
import {
  errorNotification,
  getRequest,
  trackingAccountUrl,
  trackingLinesUrl,
  trackingPricingUrl,
  trackingShipmentUrl
} from '../../config'
import AccountBanner from './AccountBanner'
import ActionLog from './ActionLog'
import AlertHistory from './AlertHistory'
import BillOfLadingPanel from './BillOfLadingPanel'
import ContactButtons from './ContactButtons'
import CustomerJourney from './CustomerJourney'
import NoteActionModal from './NoteActionModal'
import PaymentPanel from './PaymentPanel'
import ServiceTransactions from '../../components/ServiceTransactions'
import RefundModal from './RefundModal'
import RetrackModal from './RetrackModal'
import ShipsgoPanel from './ShipsgoPanel'
import UpdatesPanel from './UpdatesPanel'
import { AttentionTag, ChannelTag, KindTag, StageTag, StatusTag } from './tags'
import {
  attentionGuidance,
  attentionLabel,
  availableActions,
  contactMessage,
  errorSentence,
  formatAge,
  formatDateTime
} from './utils'

// The four modes `NoteActionModal` serves; they share one instance, so `modal` names the mode.
const NOTE_MODES = ['resolve', 'close', 'note', 'creditRefund']

// One shipment, everything about it, on one page: what the customer sees, what ShipsGo said, the
// money and what the team has already done. It is a page rather than a drawer because every
// per-shipment Telegram alert links straight here, often onto a phone at an awkward hour.
const ShipmentDetail = ({ match }) => {
  const id = match.params.id
  const [loading, setLoading] = useState(false)
  const [shipment, setShipment] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [lines, setLines] = useState([])
  const [account, setAccount] = useState(null)
  const [pricing, setPricing] = useState(undefined)
  const [modal, setModal] = useState(null)
  // The re-track answers 202 and does the work in the background, so the page watches the status
  // move off `starting` instead of asking the operator to keep pressing Reload.
  const [polling, setPolling] = useState(false)
  const [pollExhausted, setPollExhausted] = useState(false)
  const pollRef = useRef(null)

  const clearPoll = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current)
      pollRef.current = null
    }
  }

  const reload = async () => {
    const response = await getRequest(trackingShipmentUrl(id), 'shipment', setLoading)
    if (response.success) {
      setShipment(response.data)
      setNotFound(false)
      return response
    }
    if (response.error && response.error.response && response.error.response.status === 404) {
      setNotFound(true)
      return response
    }
    // Any other failure keeps the shipment already on screen: a flaky reload is not a reason to
    // lose the number somebody is reading out over the phone.
    errorNotification(errorSentence(response, 'That shipment could not be loaded.'))
    return response
  }

  // Re-read every five seconds while the shipment is still starting, twelve times at most. The
  // silent `setLoading` keeps the page from blinking through the loader on each try.
  const pollStarting = attempt => {
    if (attempt >= 12) {
      setPolling(false)
      setPollExhausted(true)
      return
    }
    pollRef.current = setTimeout(async () => {
      pollRef.current = null
      const response = await getRequest(trackingShipmentUrl(id), 'shipment', () => {})
      if (response.success) {
        setShipment(response.data)
        if (response.data.status === 'starting') {
          pollStarting(attempt + 1)
          return
        }
      }
      setPolling(false)
    }, 5000)
  }

  useEffect(() => {
    clearPoll()
    setPolling(false)
    setPollExhausted(false)
    reload()
    // eslint-disable-next-line
  }, [id])

  // A timer that outlives the page would keep fetching a shipment nobody is looking at.
  useEffect(() => clearPoll, [])

  useEffect(() => {
    // Best-effort, exactly as on the worklist: a failure leaves the setup banner silent.
    getRequest(trackingLinesUrl, 'shipping lines', () => {}).then(response => {
      if (response.success) setLines(response.data.results || response.data)
    })
    getRequest(trackingAccountUrl, 'ShipsGo account', () => {}).then(response => {
      if (response.success) setAccount(response.data)
    })
    getRequest(trackingPricingUrl('GH'), 'price', () => {}).then(response => {
      if (response.success) setPricing(response.data)
    })
  }, [])

  const closeModal = () => setModal(null)

  const onActionDone = async () => {
    setModal(null)
    await reload()
  }

  const onRefundDone = async result => {
    setModal(null)
    await reload()
    // Offered, never done: a refund and the end of tracking are two decisions, and the second one
    // needs a note of its own.
    if (result && result.offerClose) {
      Modal.confirm({
        title: 'Close this shipment too?',
        content:
          'The refund is recorded. If tracking should end, close it now with a note.',
        okText: 'Close it',
        cancelText: 'Not now',
        onOk: () => setModal('close')
      })
    }
  }

  const onRetrackDone = async result => {
    setModal(null)
    const response = await reload()
    if (!result || !result.started) return
    if (response.success && response.data.status === 'starting') {
      clearPoll()
      setPollExhausted(false)
      setPolling(true)
      pollStarting(0)
    }
  }

  if (notFound) {
    return (
      <div className="container">
        <PageTitle title="Shipment not found" />
        <Result
          status="404"
          title="That shipment could not be found."
          subTitle="It may have been deleted by the customer before payment."
          extra={<Link to="/tracking/shipments">All shipments</Link>}
        />
      </div>
    )
  }

  const row = shipment || {}
  const actions = availableActions(shipment)

  return (
    <PageLoader className="container" loading={loading}>
      {shipment ? <PageTitle title={`Shipment ${row.number}`} /> : null}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          extra={
            <Space>
              <Button icon={<ReloadOutlined />} onClick={reload}>
                Reload
              </Button>
              <Link to="/tracking/shipments">All shipments</Link>
            </Space>
          }
        >
          <Typography.Title level={4} copyable={!!row.number} style={{ marginBottom: 8 }}>
            {row.number || '—'}
          </Typography.Title>
          <Space wrap>
            <KindTag kind={row.kind} roro={row.lineRoro} />
            {/* Where the customer started it. A WhatsApp one came from a bill of lading they
                sent us, which is the panel below. */}
            <ChannelTag channel={row.channel} />
            <span>
              {row.lineName || '—'}
              {row.lineScac ? <span style={{ opacity: 0.65 }}> {row.lineScac}</span> : null}
            </span>
            <StageTag
              stage={row.stage}
              title={row.stageTitle}
              attention={row.attention}
              completion={row.completion}
            />
            <StatusTag status={row.status} completion={row.completion} />
            <AttentionTag
              kind={row.attention}
              priority={row.attentionPriority}
              since={row.attentionSince}
            />
          </Space>
          {polling ? (
            <div style={{ marginTop: 8, opacity: 0.65 }}>
              Starting — checking every 5 seconds
            </div>
          ) : null}
          {!polling && pollExhausted && row.status === 'starting' ? (
            <div style={{ marginTop: 8, opacity: 0.65 }}>
              Still starting. Reload in a minute.
            </div>
          ) : null}
        </Card>

        <AccountBanner account={account} pricing={pricing} />

        {/* Gold for every priority, urgent included: red here would read as "this customer's cargo
            is lost". The urgency is on the priority tag above, where it belongs. */}
        {row.attention ? (
          <Alert
            type="warning"
            showIcon
            message={`${attentionLabel(row.attention)} · open for ${formatAge(
              row.attentionSince
            )}`}
            description={attentionGuidance(row.attention)}
          />
        ) : null}

        <Card title="Actions">
          <Space wrap>
            <Button
              type="primary"
              disabled={!actions.retrack}
              onClick={() => setModal('retrack')}
            >
              Re-track
            </Button>
            <Button disabled={!actions.resolve} onClick={() => setModal('resolve')}>
              Resolve
            </Button>
            <Button danger disabled={!actions.close} onClick={() => setModal('close')}>
              Close
            </Button>
            <Button disabled={!actions.refund} onClick={() => setModal('refund')}>
              Record refund
            </Button>
            <Button disabled={!actions.creditRefund} onClick={() => setModal('creditRefund')}>
              ShipsGo credit refund requested
            </Button>
            <Button disabled={!actions.note} onClick={() => setModal('note')}>
              Add note
            </Button>
          </Space>
          <div style={{ marginTop: 8, opacity: 0.65 }}>
            Every action is recorded in the action log with your name.
          </div>
        </Card>

        <Card title="Customer">
          <ContactButtons phone={row.phone} message={contactMessage(shipment)} />
          <div style={{ marginTop: 8, opacity: 0.65 }}>
            {`Contact given ${formatDateTime(row.createdAt)}`}
          </div>
        </Card>

        <CustomerJourney view={row.customerView} />
        {/* Only for a shipment that came from a document, which today means a WhatsApp one. */}
        {row.billOfLading ? <BillOfLadingPanel bill={row.billOfLading} /> : null}
        <ShipsgoPanel shipment={shipment} />
        {/* The same approval the duty conversation has: a shipment paid to our merchant number
            needs a person to confirm it before tracking can start (owner, 2026-10-30). */}
        <ServiceTransactions
          service="tracking"
          reference={((shipment || {}).payment || {}).reference}
          onChanged={reload}
        />
        <PaymentPanel shipment={shipment} />
        <UpdatesPanel shipment={shipment} onChanged={reload} />
        <AlertHistory alerts={row.alerts || []} />
        <ActionLog actions={row.actions || []} />

        {/* One instance of each dialog, mounted once and shown by `modal`. Every one of them ends
            in a re-read: what is on screen is what the server holds, never a patched local copy. */}
        <RetrackModal
          visible={modal === 'retrack'}
          shipment={shipment}
          lines={lines}
          account={account}
          onClose={closeModal}
          onDone={onRetrackDone}
        />
        <NoteActionModal
          visible={NOTE_MODES.includes(modal)}
          mode={modal}
          shipment={shipment}
          onClose={closeModal}
          onDone={onActionDone}
        />
        <RefundModal
          visible={modal === 'refund'}
          shipment={shipment}
          onClose={closeModal}
          onDone={onRefundDone}
        />
      </Space>
    </PageLoader>
  )
}

ShipmentDetail.propTypes = {
  match: PropTypes.object
}

export default withRouter(ShipmentDetail)
